import { Button, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { extractErrorMessage } from '@shared/api';
import { useRegisterMutation, useVerifyMutation } from '../api/mutations';

const { Title, Text } = Typography;

interface VerifyFormValues {
  code: string;
}

interface LocationState {
  email?: string;
}

export function VerifyEmailForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const email = state.email;

  const [form] = Form.useForm<VerifyFormValues>();
  const verifyMutation = useVerifyMutation();
  const registerMutation = useRegisterMutation();
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  // Если на страницу зашли без email — нет смысла продолжать.
  if (!email) {
    return (
      <div>
        <Title level={3} style={{ marginTop: 0 }}>
          Подтверждение email
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Сначала зарегистрируйтесь.
        </Text>
        <Button type="primary" size="large" block onClick={() => navigate('/register')}>
          К регистрации
        </Button>
      </div>
    );
  }

  const handleSubmit = async (values: VerifyFormValues) => {
    try {
      await verifyMutation.mutateAsync({ email, code: values.code });
      navigate('/');
    } catch {
      // ошибка показывается ниже
    }
  };

  const handleResend = async () => {
    setResendNotice(null);
    try {
      // Бэк сейчас умеет переотправить код через повторный /register
      // (если юзер не верифицирован — кода обновляется, юзер не дублируется).
      // Когда добавится /resend-code — заменим здесь.
      // Пароль для повторного register не нужен, но эндпоинт его требует —
      // используем dummy. ВАЖНО: бэк должен в этой ветке игнорировать пароль
      // (он его не обновляет, см. EmailAuthDAL.create_user_with_email).
      // На текущий момент это работает потому что юзер уже создан и попадает в else-ветку.
      await registerMutation.mutateAsync({ email, password: 'dummy_for_resend_xx' });
      setResendNotice('Код отправлен повторно. Проверьте почту.');
    } catch (e) {
      setResendNotice(extractErrorMessage(e, 'Не удалось отправить код повторно'));
    }
  };

  const errorMessage = verifyMutation.error ? extractErrorMessage(verifyMutation.error) : null;

  return (
    <div>
      <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
        Подтверждение email
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Мы отправили код на <b style={{ color: '#1A1A1A' }}>{email}</b>. Введите его ниже.
      </Text>

      <Form<VerifyFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Form.Item
          name="code"
          label="Код из письма"
          rules={[
            { required: true, message: 'Введите код' },
            { len: 6, message: 'Код состоит из 6 цифр' },
            { pattern: /^\d{6}$/, message: 'Только цифры' },
          ]}
        >
          <Input
            size="large"
            placeholder="000000"
            maxLength={6}
            autoFocus
            inputMode="numeric"
            style={{ letterSpacing: 4, fontSize: 18, textAlign: 'center' }}
          />
        </Form.Item>

        {errorMessage && (
          <div style={{ marginBottom: 16, color: '#DC2626', fontSize: 13 }}>{errorMessage}</div>
        )}
        {resendNotice && (
          <div style={{ marginBottom: 16, color: '#059669', fontSize: 13 }}>{resendNotice}</div>
        )}

        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={verifyMutation.isPending}
        >
          Подтвердить
        </Button>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button
            type="link"
            onClick={handleResend}
            loading={registerMutation.isPending}
            style={{ padding: 0 }}
          >
            Отправить код повторно
          </Button>
        </div>

        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <Link to="/login">Назад ко входу</Link>
        </div>
      </Form>
    </div>
  );
}
